import { ReactUtils } from '@festina-life/core'
import { FloatingPortal } from '@floating-ui/react'
import cx from 'classnames'
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  useMemo,
} from 'react'

import { mergeRefs } from '../../libs/refs'
import { zIndexStyles } from '../../libs/zIndexStyles'
import { TooltipPaper } from './TooltipPaper'
import { useTooltip } from './useTooltip'

type Props = {
  /**
   * The message to display in the tooltip
   */
  tip: ReactNode | string
  /**
   * Children to display in the tooltip
   */
  children: ReactNode
  /**
   * Classname applied to trigger element wrapper.
   */
  className?: string
  /**
   * Classname applied to tooltip element.
   */
  tipClassName?: string
  /**
   * Optional ref to the tooltip trigger element.
   */
  ref?: Ref<HTMLElement>
  /**
   * OnClick handler for the tooltip - useful to let consumer e.g. stop propagation
   */
  tipOnClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  /**
   * True if the tooltip should be rendered in a portal - this ensures parents doesn't clip but there might be cases where one can't use it.
   * @defaultValue `true`
   */
  shouldRenderInPortal?: boolean
} & Pick<
  NonNullable<Parameters<typeof useTooltip>[0]>,
  | 'placement'
  | 'isInteractive'
  | 'hasMinWidthOfAnchorWidth'
  | 'hoverDelay'
  | 'offset'
>

export const Tooltip = ({
  tip,
  children,
  className,
  ref,
  tipOnClick,
  tipClassName,
  hasMinWidthOfAnchorWidth,
  shouldRenderInPortal = true,
  isInteractive = false,
  placement = 'bottom-start',
  hoverDelay,
  offset,
}: Props) => {
  const {
    refs,
    x,
    y,
    getReferenceProps,
    getFloatingProps,
    isOpen,
    setIsOpen,
    strategy,
    calculatedPlacement,
  } = useTooltip({
    isInteractive,
    hasMinWidthOfAnchorWidth,
    hoverDelay,
    offset,
    placement,
  })

  const setReferenceRef = useMemo(
    () =>
      mergeRefs((node) => {
        if (node) {
          refs.setReference(node)
        }
      }, ref),
    [ref, refs],
  )

  return (
    <>
      {/* We only render an extra dom element if we can't add tooltip props directly to the child */}
      {isValidElement(children) ? (
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- https://github.com/facebook/react/issues/31824#issuecomment-2551265055
        cloneElement(children as ReactElement<any>, {
          ref: setReferenceRef,
          ...getReferenceProps(),
          'data-amaze-isopen': isOpen,
          className: cx(
            className,
            // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- https://github.com/facebook/react/issues/31824#issuecomment-2551265055
            (children as ReactElement<any>).props?.className,
          ),
        })
      ) : (
        <span
          className={cx('inline-block', className)}
          ref={setReferenceRef}
          {...getReferenceProps()}
          data-amaze-isopen={isOpen}
        >
          {children}
        </span>
      )}
      {isOpen && tip && (
        <ReactUtils.ConditionalWrap
          condition={shouldRenderInPortal}
          wrap={(children) => <FloatingPortal>{children}</FloatingPortal>}
        >
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={{ position: strategy, top: y, left: x }}
            onClick={(event) => {
              if (isInteractive) {
                // A tip rendered into a portal is no longer a descendant in the
                // document, but it still is in the React tree, so a click in it
                // carries on up to whatever holds the tip — a table row that
                // selects a field, say, which would answer a reader working in
                // the tip by navigating away from it. Working in a tip is not
                // clicking on what it happens to be attached to.
                event.stopPropagation()
              } else {
                setIsOpen(false)
              }
              tipOnClick?.(event)
            }}
            onBlur={({ relatedTarget }) => {
              // A bit surprising that this isn't handled by useFocus but we need to manually close the tooltip when focus leaves both the reference and the floating element
              if (
                !refs.domReference.current?.contains(relatedTarget) &&
                !refs.floating.current?.contains(relatedTarget)
              ) {
                setIsOpen(false)
              }
            }}
            className={zIndexStyles('tips')}
          >
            <TooltipPaper
              isOpen={isOpen}
              placement={calculatedPlacement}
              tipClassName={tipClassName}
            >
              {tip}
            </TooltipPaper>
          </div>
        </ReactUtils.ConditionalWrap>
      )}
    </>
  )
}
